import * as React from 'react';
import { cn } from '@/utils';
import { Button } from '@/components/ui/button';
import { ChevLeftFilledIcon, ChevRightFilledIcon } from '../Icons';

export interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  currentPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  theme?: 'dark' | 'light';
}

const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  ({ className, currentPage, totalPages, onPageChange, theme = 'light', ...props }, ref) => {
    const renderPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;

      pages.push(
        <div
          key="prev"
          onClick={() => currentPage > 1 && onPageChange?.(currentPage - 1)}
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center cursor-pointer',
            theme === 'dark' ? 'bg-[#D4D4DA] text-white' : 'bg-[#F4F4F4] text-black hover:bg-[#E4E4E4] transition-colors',
            currentPage === 1 && 'opacity-50 cursor-not-allowed',
          )}
        >
          <span className="text-xl select-none">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10 3.99902L6 7.99902L10 11.999"
                stroke={currentPage === 1 ? '#9D9DA6' : '#48484B'}
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </div>,
      );

      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <div
            key={i}
            onClick={() => onPageChange?.(i)}
            className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-colors',
              'text-lg select-none',
              currentPage === i
                ? 'bg-jenmusic-content-primary text-jenmusic-background-primary'
                : 'bg-jenmusic-background-primary text-jenmusic-content-primary',
            )}
          >
            {i}
          </div>,
        );
      }

      pages.push(
        <div
          key="next"
          onClick={() => {
            currentPage < totalPages && onPageChange?.(currentPage + 1);
          }}
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center cursor-pointer',
            theme === 'dark' ? 'bg-[#D4D4DA] text-white' : 'bg-[#F4F4F4] text-black hover:bg-[#E4E4E4] transition-colors',
            currentPage === totalPages && 'opacity-50 cursor-not-allowed',
          )}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6 3.99902L10 7.99902L6 11.999"
              stroke={currentPage === totalPages ? '#9D9DA6' : '#48484B'}
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>,
      );

      return pages;
    };

    return (
      <div ref={ref} className={cn('flex items-center justify-center gap-1', className)} {...props}>
        {renderPageNumbers()}
      </div>
    );
  },
);

Pagination.displayName = 'Pagination';

export { Pagination };
